<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-5">
          {{ $t('Operation Report') }}
        </h1>
      </v-col>
    </v-row>
    <v-card
      class="mx-auto pa-5 mb-5"
    >
      <v-card-text>
        <h2 class="mb-5">
          Фильтр
        </h2>
        <v-row class="border_bottom">
          <v-col cols="6" lg="3">
            <v-select
              v-model="selectedPlatform"
              :items="platform"
              label="Платформа"
              item-text="name"
              item-value="id"
              clearable
              outlined
              prepend-icon="mdi-home"
              hide-details
            />
          </v-col>
          <v-col cols="6" lg="3">
            <v-select
              v-model="selectedClient"
              :items="clientsFilterByPlatform"
              label="Клиент"
              item-text="legal_name"
              item-value="id"
              clearable
              outlined
              prepend-icon="mdi-account-outline"
              hide-details
            />
          </v-col>
          <v-col cols="6" lg="3">
            <v-select
              v-model="selectedSupplier"
              :items="suppliersFilter"
              label="Поставщик"
              item-text="legal_name"
              item-value="id"
              clearable
              outlined
              prepend-icon="mdi-account"
              hide-details
            />
          </v-col>
        </v-row>
        <v-row class="mt-10">
          <v-col cols="6" lg="3">
            <date-range
              :local-storage-prefix="localStoragePrefix"
              @datesChanged="dates = $event"
            />
          </v-col>
          <v-col cols="6" lg="3">
            <v-select
              v-model="selectedStatusApplication"
              :items="statusApplication"
              label="Статус"
              item-text="name"
              item-value="code"
              clearable
              outlined
              prepend-icon="mdi-check-network"
              hide-details
              multiple
            />
          </v-col>
        </v-row>
        <v-row>
          <v-btn
            v-if="filtersChanged()"
            class="clear-filters-btn ml-auto"
            @click="clearFilters"
          >
            Очистить фильтры
          </v-btn>
        </v-row>
      </v-card-text>
    </v-card>
    <v-row justify="center">
      <v-btn
        color="success"
        :disabled="isDataLoading"
        @click="downloadXLSX"
      >
        Отчет в xlsx
        <v-icon
          right
          dark
        >
          mdi-download
        </v-icon>
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
import Component from 'vue-class-component';
import GetPlantsHRAndClientListing from '@/graphql/GetPlantsHRAndClientListing.gql';
import { Watch, Mixins, Prop } from 'vue-property-decorator';
import Pivot from '@/components/common/Pivot/Pivot.vue';
import LoadingDataMixin from '@/mixins/LoadingDataMixin';
import DateRange from '@/components/common/DateRange.vue';
import EntityHelperMixin from '@/mixins/EntityHelperMixin';
import { report, talendCommon } from '@/utils/config';
import Axios from 'axios';


@Component({
  components: {
    Pivot,
    DateRange,
  },
})
export default class OperationReport extends Mixins(LoadingDataMixin, EntityHelperMixin) {
  @Prop({
    type: String,
    required: false,
    default: 'OperationReport',
  }) localStoragePrefix;

  platform = [];

  clients = [];

  suppliers = [];

  statusApplication = [];

  selectedPlatform = localStorage.getItem('table-selectedPlatform') || null;

  selectedClient = null;

  selectedSupplier = null;

  selectedStatusApplication = [];

  isDataLoading = false;

  dates = ['2020-11-10', '2020-11-27'];

  defaultDates = [];

  get clientsFilterByPlatform() {
    if (this.selectedPlatform && Number(this.selectedPlatform)) {
      return this.clients.filter((i) => i.Plants && i.Plants.some((ele) => Number(ele.id) === Number(this.selectedPlatform)));
    }
    return this.clients;
  }

  get suppliersFilter() {
    if (this.selectedPlatform && Number(this.selectedPlatform)) {
      return this.suppliers.filter((i) => i.PlantsHR && i.PlantsHR.some((ele) => Number(ele.id) === Number(this.selectedPlatform)));
    }
    return this.suppliers;
  }

  async downloadXLSX() {
    this.isDataLoading = true;
    try {
      const res = await Axios.post(
        report.generateOperationReport,
        {
          platform_id: this.selectedPlatform,
          client_id: this.selectedClient,
          supplier_id: this.selectedSupplier,
          statuses: this.selectedStatusApplication,
          date_from: this.dates[0],
          date_to: this.dates[1],
        },
      );

      if (res.status === 200 && res.data && res.data.bucket && res.data.filename) {
        document.location.href = this.buildDownloadLink(res.data.bucket, res.data.filename);
      } else if (res.data.errorMessage) {
        this.$bus.$emit('notification', res.data.errorMessage, 'error');
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.isDataLoading = false;
    }
  }

  buildDownloadLink(bucket, objectName) {
    const linkConstructorForDownload = talendCommon.downloadFileFromS3;
    const authToken = localStorage.getItem('authToken');
    if (authToken !== null) {
      return linkConstructorForDownload
        .replace('{authorizationToken}', authToken)
        .replace('{bucket}', bucket)
        .replace('{file}', objectName);
    }

    return linkConstructorForDownload;
  }

  loadPlatformAndClients() {
    return this.$apollo.query({
      query: GetPlantsHRAndClientListing,
      variables: {},
    }).then((res) => {
      if (res?.data?.getPlantsHRListing?.edges?.length) {
        this.platform = res?.data?.getPlantsHRListing?.edges.map((item) => item.node);
      }
      if (res?.data?.getClientListing?.edges?.length) {
        this.clients = res?.data?.getClientListing?.edges.map((item) => item.node);
      }
      if (res?.data?.getSupplierListing?.edges?.length) {
        this.suppliers = res?.data?.getSupplierListing?.edges.map((item) => item.node);
      }
      if (res?.data?.getStatusApplicationListing?.edges?.length) {
        this.statusApplication = res?.data?.getStatusApplicationListing?.edges.map((item) => item.node);
      }
      return res;
    }).catch((e) => {
      console.error(e);
    });
  }

  calculateDefaultDates() {
    const dateNow = new Date();
    const dateAgo = new Date();
    dateAgo.setMonth(dateAgo.getMonth() - 1);
    dateAgo.setDate(1);
    this.defaultDates = [dateAgo.toISOString().split('T')[0], dateNow.toISOString().split('T')[0]];
  }

  async beforeMount() {
    this.calculateDefaultDates();
    await this.loadPlatformAndClients();
  }

  @Watch('selectedPlatform')
  selectedPlatformChanged(value) {
    this.selectedClient = null;
    this.selectedSupplier = null;
    if (typeof window !== 'undefined' && localStorage) {
      if (value) {
        localStorage.setItem('table-selectedPlatform', value);
      } else {
        localStorage.removeItem('table-selectedPlatform');
      }
    }
  }

  filtersChanged() {
    return this.selectedPlatform
      || this.selectedSupplier
      || this.selectedClient
      || this.selectedStatusApplication.length > 0
      || this.dates.length !== 2
      || this.dates[0] !== this.defaultDates[0]
      || this.dates[1] !== this.defaultDates[1];
  }

  clearFilters() {
    this.selectedStatusApplication = [];
    if (this.selectedPlatform) {
      this.selectedPlatform = null;
    } else {
      this.selectedPlatformChanged(this.selectedPlatform);
    }
    this.$bus.$emit('clear-filters');
  }
}
</script>
